import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { systemSettingsApi } from '@/services/api';
import { systemSettingsApi as newSystemSettingsApi, PaymentMethodsSettings } from '@/services/systemSettingsService';
import { platformSettingsApi, CurrencySettings } from '@/services/platformSettingsService';
import Button from '@/components/ui/Button';
import { CogIcon, CheckCircleIcon, XCircleIcon, CreditCardIcon, TruckIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const SystemSettingsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [isFreeMode, setIsFreeMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Payment method settings state
  const [paymentSettings, setPaymentSettings] = useState<PaymentMethodsSettings | null>(null);
  const [isLoadingPaymentSettings, setIsLoadingPaymentSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState<Record<string, boolean>>({});

  // Currency settings state
  const [currencySettings, setCurrencySettings] = useState<CurrencySettings>({
    currency_code: 'USD',
    currency_symbol: '$',
    currency_name: 'US Dollar',
  });
  const [isLoadingCurrency, setIsLoadingCurrency] = useState(true);
  const [isSavingCurrency, setIsSavingCurrency] = useState(false);

  useEffect(() => {
    loadFreeModeStatus();
    loadPaymentMethodSettings();
    loadCurrencySettings();
  }, []);

  const loadFreeModeStatus = async () => {
    try {
      setIsLoading(true);
      const response = await systemSettingsApi.getFreeMode();
      setIsFreeMode(response.data.is_free_mode || false);
    } catch (error: any) {
      console.error('Error loading free mode status:', error);
      toast.error('Failed to load free mode status');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPaymentMethodSettings = async () => {
    try {
      setIsLoadingPaymentSettings(true);
      const response = await newSystemSettingsApi.getPaymentMethodsSettings();
      setPaymentSettings(response.data);
    } catch (error: any) {
      console.error('Error loading payment method settings:', error);
      toast.error('Failed to load payment method settings');
    } finally {
      setIsLoadingPaymentSettings(false);
    }
  };

  const loadCurrencySettings = async () => {
    try {
      setIsLoadingCurrency(true);
      const response = await platformSettingsApi.getPublicSettings();
      setCurrencySettings({
        currency_code: response.data.currency_code,
        currency_symbol: response.data.currency_symbol,
        currency_name: response.data.currency_name,
      });
    } catch (error: any) {
      console.error('Error loading currency settings:', error);
      toast.error('Failed to load currency settings');
    } finally {
      setIsLoadingCurrency(false);
    }
  };

  const handleSaveCurrencySettings = async () => {
    try {
      setIsSavingCurrency(true);
      await platformSettingsApi.updateCurrencySettings(currencySettings);
      toast.success('Currency settings updated successfully');
      // Reload the page to apply new currency settings
      window.location.reload();
    } catch (error: any) {
      console.error('Error updating currency settings:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update currency settings');
    } finally {
      setIsSavingCurrency(false);
    }
  };

  const handleToggleFreeMode = async () => {
    try {
      setIsSaving(true);
      const newValue = !isFreeMode;
      const valueString = newValue ? 'true' : 'false';

      // Check if setting exists, if not create it
      try {
        await systemSettingsApi.getSystemSetting('free_mode');
      } catch (error: any) {
        // Setting doesn't exist, we'll create it via update
        if (error?.response?.status === 404) {
          // Create the setting by using set_value in the backend
          // For now, we'll use update which should create if not exists
        }
      }

      await systemSettingsApi.updateSystemSetting(
        'free_mode',
        valueString,
        'Enable free mode: Users don\'t pay commission and get direct access to buyer contact information',
        true
      );

      setIsFreeMode(newValue);
      toast.success(
        newValue
          ? 'Free mode enabled. Users can now access buyer contact information without paying commission.'
          : 'Free mode disabled. Commission payment is now required to access buyer contact information.'
      );
    } catch (error: any) {
      console.error('Error updating free mode:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update free mode setting');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePaymentSetting = async (settingKey: string, currentValue: boolean, settingName: string) => {
    try {
      setSavingSettings(prev => ({ ...prev, [settingKey]: true }));
      const newValue = !currentValue;

      await newSystemSettingsApi.updateSettingByKey(settingKey, {
        value: newValue ? 'true' : 'false',
        is_active: true,
      });

      // Reload settings to get updated values
      await loadPaymentMethodSettings();

      toast.success(`${settingName} ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (error: any) {
      console.error(`Error updating ${settingName}:`, error);
      toast.error(error?.response?.data?.detail || `Failed to update ${settingName}`);
    } finally {
      setSavingSettings(prev => ({ ...prev, [settingKey]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <CogIcon className="h-8 w-8 text-gray-500 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
              <p className="text-gray-600 mt-2">Configure platform-wide settings and business parameters</p>
            </div>
          </div>
        </div>

        {/* Currency Settings */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center mb-6">
            <CurrencyDollarIcon className="h-6 w-6 text-gray-500 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Currency Settings</h2>
              <p className="text-sm text-gray-600">Configure the platform's default currency</p>
            </div>
          </div>

          {isLoadingCurrency ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading currency settings...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Currency Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency Code *
                  </label>
                  <select
                    value={currencySettings.currency_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const symbols: Record<string, { symbol: string; name: string }> = {
                        USD: { symbol: '$', name: 'US Dollar' },
                        EUR: { symbol: '€', name: 'Euro' },
                        GBP: { symbol: '£', name: 'British Pound' },
                        BDT: { symbol: '৳', name: 'Bangladeshi Taka' },
                        INR: { symbol: '₹', name: 'Indian Rupee' },
                        JPY: { symbol: '¥', name: 'Japanese Yen' },
                        CNY: { symbol: '¥', name: 'Chinese Yuan' },
                        AUD: { symbol: 'A$', name: 'Australian Dollar' },
                        CAD: { symbol: 'C$', name: 'Canadian Dollar' },
                      };
                      setCurrencySettings({
                        currency_code: code,
                        currency_symbol: symbols[code]?.symbol || code,
                        currency_name: symbols[code]?.name || code,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="BDT">BDT - Bangladeshi Taka</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                  </select>
                </div>

                {/* Currency Symbol */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Symbol
                  </label>
                  <input
                    type="text"
                    value={currencySettings.currency_symbol}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                  />
                </div>

                {/* Currency Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency Name
                  </label>
                  <input
                    type="text"
                    value={currencySettings.currency_name}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Preview:</strong> Prices will be displayed as:
                </p>
                <div className="text-lg font-semibold text-blue-900">
                  {currencySettings.currency_code === 'BDT' || currencySettings.currency_code === 'INR'
                    ? `1,000.00 ${currencySettings.currency_symbol}`
                    : `${currencySettings.currency_symbol}1,000.00`}
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveCurrencySettings}
                  loading={isSavingCurrency}
                  disabled={isSavingCurrency}
                >
                  Save Currency Settings
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Free Mode Toggle */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Free Mode</h2>
              <p className="text-gray-600 text-sm mb-4">
                When enabled, sellers can access buyer contact information (name, email, phone, billing and shipping addresses) 
                without paying commission. Commission payment buttons will be hidden.
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 font-medium mb-2">When Free Mode is enabled:</p>
                <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                  <li>Sellers get direct access to buyer contact information</li>
                  <li>No commission payment required</li>
                  <li>Commission payment buttons are hidden</li>
                  <li>Buyer billing and shipping addresses are immediately visible</li>
                </ul>
              </div>
            </div>
            <div className="ml-6">
              <button
                type="button"
                onClick={handleToggleFreeMode}
                disabled={isSaving}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 
                  focus:ring-offset-2 ${isFreeMode ? 'bg-primary-600' : 'bg-gray-200'}
                  ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                role="switch"
                aria-checked={isFreeMode}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out ${isFreeMode ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
          </div>
          
          <div className="mt-4 flex items-center">
            {isFreeMode ? (
              <>
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm font-medium text-green-700">Free Mode is currently ENABLED</span>
              </>
            ) : (
              <>
                <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm font-medium text-gray-700">Free Mode is currently DISABLED</span>
              </>
            )}
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center mb-6">
            <CreditCardIcon className="h-6 w-6 text-gray-500 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
              <p className="text-sm text-gray-600">Configure which payment methods are available at checkout</p>
            </div>
          </div>

          {isLoadingPaymentSettings ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading settings...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cash on Delivery */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Cash on Delivery (COD)</h3>
                  <p className="text-sm text-gray-600">Allow customers to pay when they receive their order</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'payment_method_cod',
                    paymentSettings?.payment_methods.cod || false,
                    'Cash on Delivery'
                  )}
                  disabled={savingSettings['payment_method_cod']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.payment_methods.cod ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['payment_method_cod'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  role="switch"
                  aria-checked={paymentSettings?.payment_methods.cod}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.payment_methods.cod ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>

              {/* Prepaid Payment */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Prepaid Payment</h3>
                  <p className="text-sm text-gray-600">Allow customers to pay online before delivery</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'payment_method_prepaid',
                    paymentSettings?.payment_methods.prepaid || false,
                    'Prepaid Payment'
                  )}
                  disabled={savingSettings['payment_method_prepaid']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.payment_methods.prepaid ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['payment_method_prepaid'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  role="switch"
                  aria-checked={paymentSettings?.payment_methods.prepaid}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.payment_methods.prepaid ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Shipping Methods Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center mb-6">
            <TruckIcon className="h-6 w-6 text-gray-500 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Shipping Methods</h2>
              <p className="text-sm text-gray-600">Configure which shipping methods are available</p>
            </div>
          </div>

          {isLoadingPaymentSettings ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cash on Delivery Shipping */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Cash on Delivery</h3>
                  <p className="text-sm text-gray-600">Ship with payment on delivery</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'shipping_method_cod',
                    paymentSettings?.shipping_methods.cash_on_delivery || false,
                    'COD Shipping'
                  )}
                  disabled={savingSettings['shipping_method_cod']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.shipping_methods.cash_on_delivery ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['shipping_method_cod'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.shipping_methods.cash_on_delivery ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>

              {/* Standard Shipping */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Standard Shipping</h3>
                  <p className="text-sm text-gray-600">Regular shipping timeline</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'shipping_method_standard',
                    paymentSettings?.shipping_methods.standard || false,
                    'Standard Shipping'
                  )}
                  disabled={savingSettings['shipping_method_standard']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.shipping_methods.standard ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['shipping_method_standard'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.shipping_methods.standard ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>

              {/* Advance Payment Shipping */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Advance Payment</h3>
                  <p className="text-sm text-gray-600">Require partial payment upfront</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'shipping_method_advance',
                    paymentSettings?.shipping_methods.advance_payment || false,
                    'Advance Payment Shipping'
                  )}
                  disabled={savingSettings['shipping_method_advance']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.shipping_methods.advance_payment ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['shipping_method_advance'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.shipping_methods.advance_payment ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Delivery Methods Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center mb-6">
            <TruckIcon className="h-6 w-6 text-gray-500 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Delivery Methods</h2>
              <p className="text-sm text-gray-600">Configure which delivery methods are available</p>
            </div>
          </div>

          {isLoadingPaymentSettings ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Courier Delivery */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Courier Delivery</h3>
                  <p className="text-sm text-gray-600">Delivery by courier service</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'delivery_method_courier',
                    paymentSettings?.delivery_methods.courier || false,
                    'Courier Delivery'
                  )}
                  disabled={savingSettings['delivery_method_courier']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.delivery_methods.courier ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['delivery_method_courier'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.delivery_methods.courier ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>

              {/* Self Delivery */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Self Delivery</h3>
                  <p className="text-sm text-gray-600">Seller delivers directly to customer</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'delivery_method_self',
                    paymentSettings?.delivery_methods.self_delivery || false,
                    'Self Delivery'
                  )}
                  disabled={savingSettings['delivery_method_self']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.delivery_methods.self_delivery ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['delivery_method_self'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.delivery_methods.self_delivery ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>

              {/* Pickup */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">Pickup</h3>
                  <p className="text-sm text-gray-600">Customer picks up from seller location</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleTogglePaymentSetting(
                    'delivery_method_pickup',
                    paymentSettings?.delivery_methods.pickup || false,
                    'Pickup'
                  )}
                  disabled={savingSettings['delivery_method_pickup']}
                  className={`
                    relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                    transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500
                    focus:ring-offset-2 ${paymentSettings?.delivery_methods.pickup ? 'bg-primary-600' : 'bg-gray-200'}
                    ${savingSettings['delivery_method_pickup'] ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                      transition duration-200 ease-in-out ${paymentSettings?.delivery_methods.pickup ? 'translate-x-5' : 'translate-x-0'}
                    `}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> These settings control which payment, shipping, and delivery options are available to customers during checkout.
            Changes take effect immediately for all new orders.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SystemSettingsPage;